/**
 * Patched version of useElementBounding from vueuse.
 *
 * As of Vue 2.6.14, there seems to be a bug in useResizeObserver used by useElementBounding.
 * Vue's internal traverse function called by `watch()` inside useResizeObserver causes maximum call stack size exceeded error
 * because it tries to deeply observe Vue instances which causes infinite loop.
 * In latest version of Vue 2.7 and Vue 3, this bug is fixed.
 * 2.7: https://github.com/vuejs/vue/commit/5960f05c69099c174062b6672c7a21d717a3bccf
 * Vueuse uses `deep: true` option to support observing array of elements. `useElementBounding` does not need that, so we can safely remove it.
 * Until we drop support for Vue 2.6, we need to use this patched version.
 */

import { type MaybeComputedElementRef, unrefElement, useEventListener, tryOnMounted, type UseElementBoundingOptions, type UseElementBoundingReturn } from '@vueuse/core'
import { ref, watch } from 'vue-demi'
import { useSingleElementResizeObserver } from './use-resize-observer-patch'

/**
 * Reactive bounding box of an HTML element.
 *
 * @see https://vueuse.org/useElementBounding
 * @param target
 */
export function useElementBoundingPatch (
  target: MaybeComputedElementRef,
  options: UseElementBoundingOptions = {}
): UseElementBoundingReturn {
  const {
    reset = true,
    windowResize = true,
    windowScroll = true,
    immediate = true
  } = options

  const height = ref(0)
  const bottom = ref(0)
  const left = ref(0)
  const right = ref(0)
  const top = ref(0)
  const width = ref(0)
  const x = ref(0)
  const y = ref(0)

  function update () {
    const el = unrefElement(target)

    if (el == null) {
      if (reset) {
        height.value = 0
        bottom.value = 0
        left.value = 0
        right.value = 0
        top.value = 0
        width.value = 0
        x.value = 0
        y.value = 0
      }
      return
    }

    const rect = el.getBoundingClientRect()

    height.value = rect.height
    bottom.value = rect.bottom
    left.value = rect.left
    right.value = rect.right
    top.value = rect.top
    width.value = rect.width
    x.value = rect.x
    y.value = rect.y
  }

  /**
   * Next line is patched
   */
  useSingleElementResizeObserver(target, update)
  watch(() => unrefElement(target), ele => { (ele == null) && update() })

  if (windowScroll) { useEventListener('scroll', update, { capture: true, passive: true }) }
  if (windowResize) { useEventListener('resize', update, { passive: true }) }

  tryOnMounted(() => {
    if (immediate) { update() }
  })

  return {
    height,
    bottom,
    left,
    right,
    top,
    width,
    x,
    y,
    update
  }
}
